import { PaletteMode, Components, Theme } from '@mui/material';
import { themePalette } from '../theme.palette';
import { typographyTokens } from '../typography-tokens';
import dimensions from '../dimensions';

// MuiPickersTextField
export const MuiPickersTextFieldTheme = (
  mode: PaletteMode,
): Components<Theme>['MuiPickersTextField'] => {
  const { componentTokens } = themePalette(mode);
  const { spacing } = dimensions;

  return {
    styleOverrides: {
      root: {
        border: 'none',
        minWidth: '400px',
        '& .MuiPickersSectionList-root': {
          padding: '21px 0px 7px',
          fontSize: typographyTokens.fontSizes.sm,
          fontWeight: typographyTokens.fontWeights.regular,
          lineHeight: typographyTokens.lineHeights.sm,
        },
        '& .MuiInputLabel-root': {
          fontSize: typographyTokens.fontSizes.sm,
          fontWeight: typographyTokens.fontWeights.regular,
          color: componentTokens.inputs.colors.normal.text.inputText.resting,
          transition:
            'color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
          '&:not(.Mui-disabled):hover': {
            color: componentTokens.inputs.colors.normal.text.inputText.hover,
          },
          '&.Mui-disabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
          },
          '&.MuiInputLabel-shrink': {
            // Adjust the label position when it moves to the top
            transform: 'translate(14px, 8px) scale(0.65)',
            backgroundColor: 'transparent',
            color: componentTokens.inputs.colors.normal.text.label.filled,
            fontWeight: typographyTokens.fontWeights.semibold,
          },
          '&.Mui-focused': {
            color: componentTokens.inputs.colors.normal.text.inputText.selected,
          },
        },
        '& .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: componentTokens.inputs.colors.normal.stroke.resting,
          borderWidth: '1px',
          borderRadius: spacing(1.5), // 6px
          transition: 'border-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
          '& legend': {
            // Hide the legend gap - we want continuous border
            maxWidth: 0,
            visibility: 'hidden',
          },
        },
        '& .MuiPickersOutlinedInput-root': {
          backgroundColor: componentTokens.inputs.colors.normal.background.resting,
          borderColor: componentTokens.inputs.colors.normal.stroke.resting,
          transition:
            'border-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
          '& .MuiPickersOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
          '&:hover': {
            backgroundColor: componentTokens.inputs.colors.normal.background.hover,
          },
          '&:hover .MuiPickersOutlinedInput-notchedOutline': {
            borderWidth: '1px',
            borderColor: componentTokens.inputs.colors.normal.stroke.hover,
          },
          '&.Mui-focused': {
            backgroundColor: componentTokens.inputs.colors.normal.background.selected,
          },
          '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
            borderWidth: '1px', // Override MUI's default 2px on focus
            borderColor: componentTokens.inputs.colors.normal.stroke.selected,
          },
          '&.Mui-error': {
            backgroundColor: componentTokens.inputs.colors.error.background.resting,
          },
          '&.Mui-error .MuiPickersOutlinedInput-notchedOutline': {
            borderWidth: '1px',
            borderColor: componentTokens.inputs.colors.error.stroke.resting,
          },
          '&.Mui-disabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
          },
          '&.Mui-disabled .MuiPickersOutlinedInput-notchedOutline': {
            borderWidth: '1px',
            borderColor: componentTokens.inputs.colors.normal.stroke.resting,
          },
        },
        '& .MuiIconButton-root': {
          // Remove background circles from calendar/clear icons
          border: 'none',
          '& .MuiSvgIcon-root': {
            height: '20px',
            width: '20px',
          },
        },
      },
    },
  };
};

// MuiPickersDay - Individual day buttons in the calendar
export const MuiPickersDayTheme = (mode: PaletteMode): Components<Theme>['MuiPickersDay'] => {
  const { componentTokens } = themePalette(mode);
  const { spacing } = dimensions;

  return {
    styleOverrides: {
      root: {
        fontSize: typographyTokens.fontSizes.xs,
        lineHeight: typographyTokens.lineHeights.xs,
        fontWeight: typographyTokens.fontWeights.semibold,
        height: '32px',
        width: '32px',
        borderRadius: spacing(1), // 4px
        backgroundColor: componentTokens.calendar.colors.date.background.resting,
        borderColor: componentTokens.calendar.colors.date.stroke.resting,
        color: componentTokens.calendar.colors.date.text.resting,
        '&:hover': {
          backgroundColor: componentTokens.calendar.colors.date.background.hover,
          borderColor: `${componentTokens.calendar.colors.date.stroke.hover} !important`,
          color: componentTokens.calendar.colors.date.text.hover,
        },
        '&:focus': {
          backgroundColor: componentTokens.calendar.colors.date.background.hover,
          borderColor: componentTokens.calendar.colors.date.stroke.hover,
          color: componentTokens.calendar.colors.date.text.hover,
        },
        '&.Mui-selected': {
          backgroundColor: componentTokens.calendar.colors.date.background.selected,
          borderColor: componentTokens.calendar.colors.date.stroke.selected,
          color: componentTokens.calendar.colors.date.text.selected,
          '&:hover': {
            backgroundColor: componentTokens.calendar.colors.date.background.hover,
            borderColor: componentTokens.calendar.colors.date.stroke.hover,
            color: componentTokens.calendar.colors.date.text.hover,
          },
          '&:focus': {
            backgroundColor: componentTokens.calendar.colors.date.background.hover,
            borderColor: componentTokens.calendar.colors.date.stroke.hover,
            color: componentTokens.calendar.colors.date.text.hover,
          },
          '&:disabled': {
            backgroundColor: componentTokens.calendar.colors.date.background.resting,
            borderColor: componentTokens.calendar.colors.date.stroke.resting,
            color: componentTokens.calendar.colors.date.text.resting,
            opacity: 0.4,
          },
        },
        '&.MuiPickersDay-today': {
          border: '1px solid',
          backgroundColor: componentTokens.calendar.colors.date.background.today,
          color: componentTokens.calendar.colors.date.text.today,
          borderColor: componentTokens.calendar.colors.date.stroke.today,
          '&:hover': {
            backgroundColor: componentTokens.calendar.colors.date.background.hover,
            borderColor: componentTokens.calendar.colors.date.stroke.hover,
          },
          '&:focus': {
            backgroundColor: componentTokens.calendar.colors.date.background.hover,
            borderColor: componentTokens.calendar.colors.date.stroke.hover,
          },
          '&:disabled': {
            backgroundColor: componentTokens.calendar.colors.date.background.resting,
            borderColor: componentTokens.calendar.colors.date.stroke.resting,
            color: componentTokens.calendar.colors.date.text.resting,
            opacity: 0.4,
          },
          '&:selected': {
            backgroundColor: componentTokens.calendar.colors.date.background.selected,
            color: componentTokens.calendar.colors.date.text.selected,
            borderColor: componentTokens.calendar.colors.date.stroke.selected,
          },
        },
        '&:disabled': {
          backgroundColor: componentTokens.calendar.colors.date.background.resting,
          color: componentTokens.calendar.colors.date.text.resting,
          borderColor: componentTokens.calendar.colors.date.stroke.resting,
          opacity: 0.4,
        },
      },
    },
  };
};

// MuiDateCalendar - The calendar popup that appears when clicking the DatePicker
export const MuiDateCalendarTheme = (mode: PaletteMode): Components<Theme>['MuiDateCalendar'] => {
  const { componentTokens } = themePalette(mode);
  const { spacing } = dimensions;

  return {
    styleOverrides: {
      root: {
        border: '1px solid',
        backgroundColor: componentTokens.calendar.colors.container.background.default,
        borderColor: componentTokens.calendar.colors.container.stroke.default,
        borderTopLeftRadius: spacing(1.5), // 6px
        borderTopRightRadius: spacing(1.5), // 6px
        width: '100%',
        height: 'auto',
        '& .MuiIconButton-root': {
          padding: 0,
          border: 'none',
        },
        '& .MuiDayCalendar-header': {
          borderTop: '1px solid',
          '& .MuiDayCalendar-weekDayLabel': {
            marginLeft: spacing(1),
            marginRight: spacing(1),
            color: componentTokens.calendar.colors.label.text.default,
            fontWeight: typographyTokens.fontWeights.semibold,
            fontSize: typographyTokens.fontSizes.xs,
            lineHeight: typographyTokens.lineHeights.xxs,
          },
          borderColor: componentTokens.calendar.colors.container.stroke.default,
          '& .MuiTypography-root': {
            color: componentTokens.calendar.colors.date.text.today,
          },
        },
        '& .MuiDayCalendar-weekContainer': {
          gap: spacing(2),
          margin: spacing(2),
        },
      },
    },
  };
};

// MuiPickersLayout - The layout wrapper for the picker (contains divider)
export const MuiPickersLayoutTheme = (mode: PaletteMode): Components<Theme>['MuiPickersLayout'] => {
  const { componentTokens } = themePalette(mode);
  const { spacing } = dimensions;

  return {
    styleOverrides: {
      root: {
        display: 'flex',
        flexDirection: 'column',
        '& .MuiDivider-root': {
          display: 'none', // Hide divider between date and time sections
        },
        // This is required to avoid applying style artifacts on DateRangePicker action bar
        '& > .MuiDialogActions-root ': {
          backgroundColor: componentTokens.calendar.colors.container.background.default,
          border: '1px solid',
          borderColor: componentTokens.calendar.colors.container.stroke.default,
          borderBottomLeftRadius: spacing(1.5), // 6px
          borderBottomRightRadius: spacing(1.5), // 6px
          borderTop: 'none',
        },
      },
    },
  };
};

export const MuiDialogActionsTheme = (mode: PaletteMode): Components<Theme>['MuiDialogActions'] => {
  const { componentTokens } = themePalette(mode);
  const { spacing } = dimensions;

  return {
    styleOverrides: {
      root: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: spacing(2),
        padding: spacing(4),
        '& .MuiButton-root': {
          outline: 'none',
          transition:
            'border-color 100ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, background-color 100ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
          border: '1px solid',
          letterSpacing: 'normal',
          textTransform: 'none',
          borderRadius: `${spacing(1.5)}px`, // 6px
          paddingLeft: spacing(4), // 12px
          paddingRight: spacing(4), // 12px
          paddingTop: spacing(2), // 8px
          paddingBottom: spacing(2), // 8px
        },
        // Cancel button - secondary variant
        '& .MuiButton-root:first-of-type': {
          color: componentTokens.buttons.colors.coreIcon.ghost.text.resting,
          backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.resting,
          borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.resting,
          '&:hover': {
            backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.hover,
          },
          '&:focus': {
            backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.selected,
          },
          '&:active': {
            backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.selected,
          },
        },
        // Apply button - primary variant (second button, or last if no clear button)
        '& .MuiButton-root:nth-of-type(2)': {
          backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.resting,
          borderColor: componentTokens.buttons.colors.coreIcon.secondary.stroke.resting,
          '&:hover': {
            backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.secondary.stroke.hover,
          },
          '&:focus': {
            backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.secondary.stroke.selected,
          },
          '&:active': {
            backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.secondary.stroke.selected,
          },
          '&:disabled': {
            backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.resting,
            borderColor: componentTokens.buttons.colors.coreIcon.secondary.stroke.resting,
            opacity: 0.4,
          },
        },
        // Clear button - link style (underlined text button, last when present)
        '& .MuiButton-root:nth-of-type(3)': {
          flexBasis: '100%',
          border: 'none',
          textDecoration: 'underline',
          height: 'auto',
          padding: '0px',
          margin: '0px',
          color: componentTokens.buttons.colors.coreIcon.ghost.text.resting,
          fontSize: typographyTokens.fontSizes.sm,
          backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.resting,
          borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.resting,
          '&:hover': {
            backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.hover,
          },
          '&:focus': {
            backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.selected,
          },
          '&:active': {
            backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.selected,
          },
        },
      },
    },
  };
};
